import {
  ComplianceCalendarStages,
  CorporationType,
  Country,
  Frequency,
  IncorporationRequestStatus,
  RootFiConnectionStatus,
  RootfiSyncStatus,
  SalesTaxDataSource,
  TransactionPricingMode,
} from '@prisma/client';
import { IAddress } from '../address.types';
import { USStatesAndTerritories } from '../countries/us';
import { Month, Quarter } from '../date-time';
import {
  AccountingBasis,
  AccountingFrequency,
  BusinessPurposeEnum,
  CorporationOnboardingStages,
  HoldingType,
  IndustryOptions,
  Marketplace,
  TaxAndAccountingOnboardingStages,
  TaxClassificationEntityTypeEnum,
} from './enums';

// ------------------------------------------------------------------------------------------------
// General Corporation Info
// ------------------------------------------------------------------------------------------------
export enum TaxIDType {
  // COUNTRY_AGENCY_TYPE
  US_EIN = 'US_EIN',
  // US tax ID for international taxpayers:
  US_ITIN = 'US_ITIN',
  // Indian tax ids:
  IN_PAN = 'IN_PAN',
  IN_TAN = 'IN_TAN',
  IN_GSTIN = 'IN_GSTIN',
  // UAE tax ID:
  AE_TRN = 'AE_TRN',
  // UK tax ID:
  GB_UTR = 'GB_UTR',
  // Ireland:
  IE_CRN = 'IE_CRN',
  // Canada business number - weird edge case - this is used as both Tax ID and uniqueIdentifierFromJurisdiction;
  CA_BN = 'CA_BN',
  // Singapore Permanent Account Number:
  SG_PAN = 'SG_PAN',
}

export type TaxID = {
  type: TaxIDType;
  name: string;
  value: string;
};

export type UniqueIdentifierFromJurisdiction = {
  // Name of the unique identifier for this jurisdiction. Ex: EIN, Company Registration Number.
  name: string;
  // Value of the unique identifier for this corporation. Ex: 12-3456789.
  // Undefined when the value is not known.
  value?: string | undefined;
};

export type TIndustryCode = {
  // Name of the identifier for this corporation. Ex: NAICS.
  name: string;
  // Value of the unique identifier for this corporation.
  // Undefined when the value is not known.
  value: string | undefined;
  displayName: string;
};

export type IRegisteredAgent = {
  name: string;
  address: IAddress;
  id: string;
};

// ------------------------------------------------------------------------------------------------
// Accounting Info
// ------------------------------------------------------------------------------------------------
/** The content's vocabulary for how a legal form's year end is decided; the tokens are its own. */
export enum FiscalYearEndType {
  FIXED_DATE = 'FIXED_DATE',
  ENTITY_CHOSEN = 'ENTITY_CHOSEN',
  INCORPORATION_ANNIVERSARY_MONTH_END = 'INCORPORATION_ANNIVERSARY_MONTH_END',
}

type FiscalYearEndData = {
  FIXED_DATE?: {
    month: Month;
    day: number;
  };
};

export type FiscalYearEnd = {
  type: FiscalYearEndType;
  // All fields in 'data' should be optional, and exactly one should be set.
  data: FiscalYearEndData;
};

/**
 * What the content says about this entity's year end, as against what the entity answered.
 *
 * It sits beside `fiscalYear` rather than inside it because an entity that has answered nothing
 * still has a rule, and the caller still needs to know whether it may ask.
 */
export type FiscalYearEndRule = {
  defaultType: FiscalYearEndType;
  /** The end the content names. Only a FIXED_DATE rule has one. */
  default?: { month: Month; day: number };
  /**
   * Whether any lawful path exists to a different year end. Existential, not typical-case: a rule
   * open only to a subclass, or needing approval, still counts as modifiable.
   */
  isModifiable: boolean;
  /** The source's own words for how selection or modification works. */
  modificationRuleSummary: string | null;
  /** The longest first period the form allows, where the source gives one. */
  firstPeriodMaxMonths: number | null;
};

export type TBankAccount = {
  id: string;
};

export type AEAccountingInfo = {
  vatEvaluationPeriod: string;
  firstVATPeriodStartMonth: Month;
};

export type CountrySpecificAccountingInfo = {
  [Country.AE]?: AEAccountingInfo;
};

// ------------------------------------------------------------------------------------------------
// Locations Info
// ------------------------------------------------------------------------------------------------
export enum BusinessLocationType {
  EMPLOYEE_HOME_ADDRESS = 'EMPLOYEE_HOME_ADDRESS',
  OPERATIONS = 'OPERATIONS',
  INVENTORY_STORAGE = 'INVENTORY_STORAGE',
  MAILING_ADDRESS = 'MAILING_ADDRESS',
}

// For now we'll just return everything as type: OPERATIONS.
// We'll implement the other ones when we get to it.
export type IBusinessLocation = {
  name?: string;
  isPrimary: boolean;
  address: IAddress;
  type: BusinessLocationType[];
};

// ------------------------------------------------------------------------------------------------
// Onboarding Info
// ------------------------------------------------------------------------------------------------
export type USOnboardingInfo = {
  hasEmployeesOrContractors?: boolean;
  isAtLeast25PercentForeignOwned?: boolean;
  hasForeignSubsidiaries?: boolean;
  willOweMoreThan500DollarsInFederalTaxes?: boolean;
  operationalStates?: string[];
  taxedAs?: TaxClassificationEntityTypeEnum;
};
export type INOnboardingInfo = {
  operationalStates?: string[];
  industry?: IndustryOptions;
};
export type SGOnboardingInfo = {
  hasEmployeesOrContractors?: boolean;
  operationalCountries?: Country[];
};
export type IEOnboardingInfo = {
  registrationNumber?: string; // is this the correct place for this?
};

export type EntityOnboardingInfo = {
  currentStageOfCorporationInOnboardingProcess?: CorporationOnboardingStages;
  stagesOfCorporationOnboardingProcessesFinished?: CorporationOnboardingStages[];
  optForRegisteredAgent?: boolean;
  /** Whether the customer may read the documents their registered agent receives. */
  registeredAgentDocumentAccess?: boolean;
  operationalCountries?: Country[]; // for transfer pricing
  // At most one of these will be set. In many cases, (for example, a country we haven't implemented onboarding for yet), zero of them will be set.
  countrySpecificFields: {
    [Country.US]?: USOnboardingInfo;
    [Country.IN]?: INOnboardingInfo;
    [Country.SG]?: SGOnboardingInfo;
    [Country.IE]?: IEOnboardingInfo;
  };
};

// ------------------------------------------------------------------------------------------------
// Sales Tax Info
// ------------------------------------------------------------------------------------------------
export type SalesTaxOnboardingInfo = {
  // Used to store the platforms the sales is made by the entity. e.g. Amazon, Walmart etc.
  marketPlace: Marketplace[];
};

// ------------------------------------------------------------------------------------------------
// Compliance Info
// ------------------------------------------------------------------------------------------------
export type USComplianceOnboardingInfo = {
  // fields can be duplicated from the onboarding info
  jurisdictionRegistrations?: string[];
  hasMoreThan500DollarsInFederalTaxes?: boolean;
  hasElectedSCorporationTaxStatus?: boolean;
  isSingleOwnerLLC?: boolean;
  hasForeignSubsidiaries?: boolean;
  hasForeignBankAccountsOver10k?: boolean;
  hasFullTimeUSEmployees?: boolean;
  providesEmployeeBenefitsToFullTimeUSEmployees?: boolean;
  hasContractorPaymentsOver600?: boolean;
  hasForeignPayments?: boolean;
  hasEmployeeStockPurchaseProgram?: boolean;
  hasDiscountedStockPurchasePlan?: boolean;
  hasQuarterlyWagesOver1500?: boolean;
  has20WeeksWithEmployees?: boolean;
  withholdsEmployeeTaxesOrSSOrMedicare?: boolean;
  hasIRSForm944Notification?: boolean;
  submitsIRSInformationReturnsByMail?: boolean;
  isApplicableLargeEmployer?: boolean;
  hasEmployeesOrContractors?: boolean;
};

export type ComplianceOnboardingInfo = {
  // At most one of these will be set. In many cases, (for example, a country we haven't implemented onboarding for yet), zero of them will be set.
  countrySpecificFields: {
    [Country.US]?: USComplianceOnboardingInfo;
  };
  taxAndAccountingOnboardingInformation?: {
    status?: IncorporationRequestStatus;
    stagesFinished?: TaxAndAccountingOnboardingStages[];
  };
};

/** What the product records about one entity's ownership, decided by the backend from its legal form. */
export type EntityOwnership = {
  isEnabled: boolean;
  /** Null where ownership is not recorded at all. */
  holdingType: HoldingType | null;
  tracksUnits: boolean;
  supportsMultipleClasses: boolean;
};

// Documentation here: https://www.notion.so/commenda/Business-Entities-164b5594f5ca806aa2c5c92fae5e958d
export type IBusinessEntity = {
  id: number;
  generalCorporationInfo: {
    legalName: string;
    entityEmail?: string;
    companyId: number;
    // ISO-8601 date string:
    formationDate?: string;
    // Address registered with the incorporation jurisdiction.
    registeredAddress?: IAddress;
    businessEntityType: CorporationType;
    /** The content legal form the entity is stamped with, e.g. GLEIF:H0PO. */
    entityLegalFormId?: string | null;
    /** The governance arrangement the entity elected, where its form offers a choice. */
    governanceStructureCode?: string | null;
    /** Only on the single-entity read. Render it; don't derive it from the entity type. */
    ownership?: EntityOwnership;
    incorporationCountry: Country;
    jurisdiction: string | null;
    // Contact phone for use by the incorporation jurisdiction. E.164 standard format.
    contactPhone?: string;
    // In Canada and probably other jurisdictions, a single value is used for uniqueIdentifierFromJurisdiction and taxId.
    uniqueIdentifierFromJurisdiction: UniqueIdentifierFromJurisdiction;
    taxIds?: TaxID[];
    primaryTaxIdType?: TaxIDType;
    publicFacingWebsiteUrl?: string;
    industryCode: TIndustryCode | undefined;
    businessDescription?: string;
    businessPurpose?: BusinessPurposeEnum;
    // Optional if we don't have a registered agent yet
    registeredAgent?: IRegisteredAgent;
    dateOfClosure?: string;
    rootfiCompanies: {
      id: number;
      connectionStatus: RootFiConnectionStatus;
      syncStatus: RootfiSyncStatus;
    }[];
  };
  // For now we should just filter on operationsInfo.
  locationsInfo?: {
    // an onboarding info field
    hasSamePhysicalAndMailingAddress?: boolean;
    primaryMailingAddress?: IAddress;
    // TODO: migrate primaryMailingAddress to locations.
    locations?: IBusinessLocation[];
  };
  accountingInfo: {
    fiscalYear?: FiscalYearEnd;
    /** The stored start of the fiscal year. `fiscalYear` above is the end it works out to. */
    fiscalYearStart?: { day: number; month: number };
    /** What the content allows, which is a different question from what the entity answered. */
    fiscalYearRule?: FiscalYearEndRule;
    accountingBasis?: AccountingBasis;
    accountingFrequency?: AccountingFrequency;
    countrySpecificFields: CountrySpecificAccountingInfo;
    // Only populated if requested:
    bankAccounts?: TBankAccount[];
    // only finding the first assigned CA Firm. Same logic as frontend but multiple CA firms can be assigned to a corporation.
    accountingFirm?: {
      id: string;
      name: string;
      complianceStartDate?: string;
      corporationId: number;
    };
  };
  stakeholderId?: string;
  // Let's make the caller specifically request these if they want them.
  featureSpecificInfo: {
    salesTax?: {
      // Eventually this will come from a Chargebee integration, for now though it's just directly stored on the corp.
      isEnabled: boolean;
      salesTaxCorporationId?: string;
      requestedNewSalesTaxRegistrations?: USStatesAndTerritories[];
      isSalesTaxOnboardingComplete?: boolean;
      salesTaxCSVKeyValueMap?: Record<string, any>;
      salesTaxDueDate?: number;
      onboardingInformation: SalesTaxOnboardingInfo;
      transactionPricingMode?: TransactionPricingMode;
      salesTaxDataSource?: SalesTaxDataSource;
    };
    bookkeeping?: {
      // Eventually this will come from a Chargebee integration, for now though it's just directly stored on the corp.
      isEnabled: boolean;
      bookkeepingFrequency?: Frequency | null;
    };
    compliance?: {
      isEnabled: boolean;
      // Fiscal years an admin has toggled Commenda-managed for this entity, descending.
      adminToggledCommendaManagedFiscalYears: number[];
      onboardingInfo: ComplianceOnboardingInfo;
      tentativeComplianceCalendarStatus?: ComplianceCalendarStages;
      // True when the entity has at least one compliance item (a non-empty calendar).
      hasComplianceItems?: boolean;
      isDueForSinglePaidComplianceCall?: boolean;
      complianceCalendar: {
        quarters: Record<
          Quarter,
          {
            effectiveStartDate: string;
            effectiveEndDate: string;
          }
        >;
      };
    };
    incorporation?: {
      isIncorporationInProgress: boolean;
    };
  };
  // The data in here is 1 level above total junk. Do not rely on it for anything other than 'the customer told us this when they were onboarding'.
  // Do not condition product logic on it after onboarding.
  onboardingInfo?: EntityOnboardingInfo;
  structure?: {
    authorizedShares: number;
    initialSharePrice: number;
    esopSharePercentage: number;
  };

  lastUpdateTime: Date;
};

// Extensions:
// Shareholders, directors, etc
// Documents
// Compliance filings
// Addresses other than incorp address out of scope - incorp address is just a field on the corp.

// incorp addresses
// operating addresses - OperatingLocations

export type IBusinessEntities = {
  entities: IBusinessEntity[];
};

/** What GET /business-entities/summaries returns for each entity: enough to name it and place it. */
export type IBusinessEntitySummary = {
  id: number;
  legalName: string | null;
  dbaName: string | null;
  incorporationCountry: Country;
};

export type IBusinessEntitySummaryPage = {
  entities: IBusinessEntitySummary[];
  nextCursor: number | null;
};

/** Country-specific routing credentials stored in BankAccount.credentials (JSONB). */
export interface IBankAccountCredentials {
  accountNumber?: string;
  routingNumber?: string;
  iban?: string;
  accountType?: string;
  sortCode?: string;
  ifscCode?: string;
  bankCode?: string;
  transitNumber?: string;
  swiftBic?: string;
  bsb?: string;
  institutionNumber?: string;
  branchCode?: string;
  clearingCode?: string;
  clabe?: string;
  cbu?: string;
  cci?: string;
  branchName?: string;
  accountOwnerName?: string;
}

// ─── Fields config (served from GET /bankAccount/fields-config) ───────────────

export interface FieldValidationRule {
  pattern?: string;
  min_length?: number;
  max_length?: number;
  length?: number;
}

export interface FieldDefinitionEntry {
  label: string;
  type: string;
  description?: string;
  required_globally?: boolean;
  validation?: FieldValidationRule;
  options?: string[];
}

export interface CountryConfigEntry {
  name: string;
  primary_currency: string;
  account_pattern: string;
  required_fields: string[];
  optional_fields: string[];
  field_overrides?: Record<
    string,
    {
      label?: string;
      validation?: FieldValidationRule;
      description?: string;
    }
  >;
  notes?: string;
}

export interface FieldsConfig {
  field_definitions: Record<string, FieldDefinitionEntry>;
  countries: Record<string, CountryConfigEntry>;
  account_patterns?: Record<string, unknown>;
  _meta?: Record<string, unknown>;
}

/**
 * Where a legal form's primary governance authority sits.
 *
 * Chosen by legal architecture, never by what the local title translates to: an LLP's partners are
 * PARTICIPANT_MANAGED, not GENERAL_PARTNERS, however they are addressed.
 */
export enum GoverningBodyType {
  BOARD_GOVERNED = 'BOARD_GOVERNED',
  APPOINTED_MANAGEMENT = 'APPOINTED_MANAGEMENT',
  GENERAL_PARTNERS = 'GENERAL_PARTNERS',
  PARTICIPANT_MANAGED = 'PARTICIPANT_MANAGED',
  TRUSTEES = 'TRUSTEES',
}

/**
 * One arrangement a legal form permits, from entity-management content.
 *
 * The three local terms are for labelling only — branch on governingBodyType, because the
 * correlation between a local title and the architecture behind it breaks across jurisdictions.
 */
export interface IGovernanceStructure {
  structureCode: string;
  isDefault: boolean;
  governingBodyType: GoverningBodyType;
  /** The arrangement's own name: "manager-managed", "administrador único". */
  structureLocalTerm: string;
  /** What the governing organ is called: "board of managers", "consejo de administración". */
  bodyLocalTerm: string;
  /** What one member of it is called: "manager", "partner", "président". */
  officeholderLocalTerm: string;
  minMembers: number | null;
  maxMembers: number | null;
  minIndividualMembers: number | null;
  permittedMemberCategories: string[];
  entityMemberRequiresIndividualRepresentative: boolean | null;
  compositionRuleSummary: string | null;
}

/**
 * What a legal form permits, and what applies where the entity has elected nothing.
 *
 * No default means the form makes the entity choose — a Spanish SL picks among four administration
 * modes — so the picker has to be shown rather than an arrangement assumed.
 */
export interface IGovernanceStructureRules {
  permitted: IGovernanceStructure[];
  default?: IGovernanceStructure;
}
